<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { Ref } from '@/helpers/Decorators';
import ProcessService, { TaskErrorLog } from '@/modules/low-code/process/services/ProcessService';
import { DateTime } from 'luxon';

@Options({
    name: 'process-log-details-modal'
})
export default class ProcessLogDetailsModal extends Vue
{
    public loading = false;
    public logDetails: TaskErrorLog | null = null;
    public currentLogId: number | null = null;

    @Ref('modal')
    public modal: () => any;

    public close(): void
    {
        this.currentLogId = null;
        this.logDetails = null;
    }

    public async loadLogDetails(logId: number): Promise<void>
    {
        this.loading = true;
        this.currentLogId = logId;
        this.logDetails = null;

        try
        {
            this.logDetails = await ProcessService.getProcessLogDetails(logId);
        }
        catch (error)
        {
            this.handleException(error, {
                0: () => this.$alert.danger(this.$t('[[[Nie udało się pobrać szczegółów logu]]]')),
            });
            this.logDetails = null;
        }
        finally
        {
            this.loading = false;
        }
    }

    public async openWithLogId(logId: number): Promise<void>
    {
        this.modal().show();
        await this.loadLogDetails(logId);
    }

    public get documentUrl(): string
    {
        if (!this.logDetails?.document) return '#';

        return this.$router.resolve({
            name: 'module-form-details',
            params: {
                licence: this.logDetails.document.baseUrl,
                publicId: this.logDetails.document.publicId
            }
        }).href;
    }
}
</script>

<template>
    <IdeoModal
        ref="modal"
        :title="$t('[[[Szczegóły logu procesu]]]')"
        hide-footer
        size="lg"
        @close="close"
    >
        <div v-if="loading" class="text-center py-4">
            <IdeoSpinner />
            <p class="mt-2">{{ $t('[[[Ładowanie szczegółów...]]]') }}</p>
        </div>

        <div v-else-if="logDetails" class="process-log-details">
            <!-- Date Created -->
            <div class="log-field mb-3">
                <h6 class="text-primary mb-2">
                    <i class="fas fa-calendar me-2"></i>
                    {{ $t('[[[Data wykonania]]]') }}
                </h6>
                <div class="field-content">
                    <p class="mb-0">{{ $filters.datetime(logDetails.dateCreatedUtc) }}</p>
                </div>
            </div>

            <!-- Document Link -->
            <div v-if="logDetails.document" class="log-field mb-3">
                <h6 class="text-primary mb-2">
                    <i class="fas fa-file-alt me-2"></i>
                    {{ $t('[[[Dokument]]]') }}
                </h6>
                <div class="field-content">
                    <p class="mb-0">
                        <RouterLink
                            :to="documentUrl"
                            class="text-decoration-none"
                            target="_blank"
                        >
                            <i class="fas fa-external-link-alt me-1"></i>
                            {{ logDetails.document.title }}
                        </RouterLink>
                    </p>
                </div>
            </div>

            <!-- Error Message -->
            <div class="log-field mb-3">
                <h6 class="text-danger mb-2">
                    <i class="fas fa-exclamation-triangle me-2"></i>
                    {{ $t('[[[Komunikat błędu]]]') }}
                </h6>
                <div class="field-content error-message">
                    <p class="mb-0">{{ logDetails.message }}</p>
                </div>
            </div>

            <!-- Stack Trace -->
            <div class="log-field">
                <h6 class="text-danger mb-2">
                    <i class="fas fa-code me-2"></i>
                    {{ $t('[[[Stack trace]]]') }}
                </h6>
                <div class="field-content error-stack-trace">
                    <pre><code>{{ logDetails.stackTrace }}</code></pre>
                </div>
            </div>
        </div>

        <div v-else-if="!loading && !logDetails" class="text-center py-4">
            <i class="fas fa-info-circle text-muted" style="font-size: 2rem;"></i>
            <p class="mt-2 text-muted">{{ $t('[[[Nie znaleziono szczegółów logu]]]') }}</p>
        </div>
    </IdeoModal>
</template>

<style lang="scss" scoped>
.process-log-details {
    .log-field {
        h6 {
            font-weight: 600;
            font-size: 0.9rem;
            text-transform: uppercase;
            letter-spacing: 0.5px;
        }

        .field-content {
            background-color: var(--bs-gray-100);
            border: 1px solid var(--bs-gray-300);
            border-radius: 0.375rem;
            padding: 1rem;
        }

        p {
            font-size: 0.95rem;
            line-height: 1.5;
        }

        a {
            color: var(--bs-primary);

            &:hover {
                color: var(--bs-primary-dark);
                text-decoration: underline !important;
            }
        }

        .error-message {
            background-color: var(--bs-danger-bg-subtle);
            border: 1px solid var(--bs-danger-border-subtle);
        }

        .error-stack-trace {
            pre {
                background-color: var(--bs-gray-100);
                border: 1px solid var(--bs-gray-300);
                border-radius: 0.375rem;
                padding: 1rem;
                margin: 0;
                font-size: 0.875rem;
                line-height: 1.5;
                overflow-x: auto;
                white-space: pre-wrap;
                word-wrap: break-word;
            }

            code {
                color: var(--bs-gray-700);
                font-family: 'Courier New', Courier, monospace;
            }
        }
    }
}
</style>
